import qs from 'qs';

import { definitions, paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import { isRecord } from '~/utils/isRecord';
import { getMembershipYearEndDate, isLuxLoyaltyEnabled } from '~/utils/luxLoyalty';
import { parseEarnDecisions } from '~/utils/luxLoyaltyChoiceCampaigns';

import { request } from '../common';

import { BASE_PATH } from './constants';

export type LoyaltyAccountSummary = definitions['accountSummary'];

export type GetLoyaltyAccountResponse =
  paths['/api/lux-loyalty/account']['get']['responses']['200']['content']['application/json'];

export type CreateLoyaltyAccountRequest =
  paths['/api/lux-loyalty/account']['post']['requestBody']['content']['application/json'];

export type CreateLoyaltyAccountResponse =
  paths['/api/lux-loyalty/account']['post']['responses']['201']['content']['application/json'];

export type UpdateInfiniteProfileRequest =
  paths['/api/lux-loyalty/account/{customerId}/infinite-profile']['patch']['requestBody']['content']['application/json'];

export type UpdateInfiniteProfileResponse =
  paths['/api/lux-loyalty/account/{customerId}/infinite-profile']['patch']['responses']['200']['content']['application/json'];

export type TierChangeRequestBody =
  paths['/api/lux-loyalty/account/tier-change']['post']['requestBody']['content']['application/json'];

export type TierChangeResponse =
  paths['/api/lux-loyalty/account/tier-change']['post']['responses']['200']['content']['application/json'];

export type SetAccountCustomerServiceRepBody =
  paths['/api/lux-loyalty/account/{customerId}/customer-service-rep']['post']['requestBody']['content']['application/json'];

export type LoyaltyAccountStatus = definitions['accountSummary']['accountStatus'];

export type AccountStatusUpdateRequestBody =
  paths['/api/lux-loyalty/account/{customerId}/status']['patch']['requestBody']['content']['application/json'];

export type AccountStatusUpdateResponse =
  paths['/api/lux-loyalty/account/{customerId}/status']['patch']['responses']['200']['content']['application/json'];

export type AccountChangeLogResponse =
  paths['/api/lux-loyalty/account/{customerId}/change-log']['get']['responses']['200']['content']['application/json'];

export type AccountChange = AccountChangeLogResponse['result']['changes'][number];
export type AccountChangeAction = AccountChange['action'];

type RequestOptions = {
  signal?: AbortSignal;
};

type GetLuxLoyaltyAccountResponse = LoyaltyAccountSummary;

type GetLuxLoyaltyActivityLogResponse =
  paths['/api/lux-loyalty/account/activities']['get']['responses']['200']['content']['application/json'];

type AccountUpdateRegionRequest =
  paths['/api/lux-loyalty/account/region']['patch']['requestBody']['content']['application/json'];

type AccountUpdateRegionResponse =
  paths['/api/lux-loyalty/account/region']['patch']['responses']['200']['content']['application/json'];

type LuxLoyaltyAccountRegions = definitions['accountRegions'];

type AccountInternalCreateResponse =
  paths['/api/lux-loyalty/account/internal']['post']['responses']['201']['content']['application/json'];

export type AccountInternalCreateRequest =
  paths['/api/lux-loyalty/account/internal']['post']['requestBody']['content']['application/json'];

export type InfiniteOnboardingEmailResponse =
  paths['/api/lux-loyalty/account/{customerId}/infinite-onboarding-email']['post']['responses']['200']['content']['application/json'];

type InfiniteOnboardingEmailWarningResponse =
  | AccountInternalCreateResponse
  | TierChangeResponse
  | InfiniteOnboardingEmailResponse;

type InfiniteOnboardingEmailStatus = NonNullable<
  NonNullable<InfiniteOnboardingEmailWarningResponse['result']['sideEffects']>['infiniteOnboardingEmail']
>;

type InfiniteOnboardingEmailReason = Extract<InfiniteOnboardingEmailStatus, { reason: string }>['reason'];

const infiniteOnboardingEmailFailureMessages: Record<NonNullable<InfiniteOnboardingEmailReason>, string> = {
  account_not_found: 'Loyalty account was not found.',
  customer_service_rep_not_found: 'Assigned concierge could not be found.',
  email_verification_error: 'Customer email verification failed before sending.',
  missing_customer_service_rep: 'Customer has no assigned concierge.',
  notification_proxy_error: 'Notification service failed to send the email.',
};

export function getInfiniteOnboardingEmailWarning(response: InfiniteOnboardingEmailWarningResponse): string | null {
  const emailStatus = response.result?.sideEffects?.infiniteOnboardingEmail;

  if (!emailStatus || emailStatus.status === 'sent') {
    return null;
  }

  const reasonMessage = emailStatus.reason ? infiniteOnboardingEmailFailureMessages[emailStatus.reason] : null;
  return `Infinite onboarding email was not sent${reasonMessage ? `: ${reasonMessage}` : '.'}`;
}

export const luxLoyaltyRegions: Record<LuxLoyaltyAccountRegions, string> = {
  AU: 'Australia',
  CA: 'Canada',
  CN: 'China',
  FR: 'France',
  DE: 'Germany',
  HK: 'Hong Kong',
  IN: 'India',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IT: 'Italy',
  JP: 'Japan',
  KR: 'South Korea',
  MO: 'Macao',
  MY: 'Malaysia',
  NL: 'Netherlands',
  NZ: 'New Zealand',
  PH: 'Philippines',
  QA: 'Qatar',
  SA: 'Saudi Arabia',
  SG: 'Singapore',
  ZA: 'South Africa',
  ES: 'Spain',
  TW: 'Taiwan',
  TH: 'Thailand',
  AE: 'United Arab Emirates',
  GB: 'United Kingdom',
  US: 'United States',
  VN: 'Vietnam',
} as const;

function mapAccountSummaryToLuxLoyaltyAccount({
  accountSummary,
  earnDecisions,
}: FetchedAccount): App.LuxLoyaltyAccount | null {
  if (!accountSummary) {
    return null;
  }

  return {
    customerId: accountSummary.customerId,
    accountStatus: accountSummary.accountStatus,
    points: accountSummary.points,
    // Optional in the contract: an account summary built without an IBS response carries no
    // breakdown, so consumers must fall back to the aggregates.
    basePoints: accountSummary.basePoints,
    bonusPoints: accountSummary.bonusPoints,
    tier: accountSummary.tier,
    statusCredits: accountSummary.statusCredits,
    baseStatusCredits: accountSummary.baseStatusCredits,
    bonusStatusCredits: accountSummary.bonusStatusCredits,
    ibsMemberId: accountSummary.ibsMemberId,
    accountNumber: accountSummary.accountNumber ?? undefined,
    createdAt: accountSummary.createdAt ?? undefined,
    statusReviewDate: accountSummary.statusReviewDate ?? undefined,
    membershipYearEndDate:
      (accountSummary as GetLuxLoyaltyAccountResponse & { membershipYearEndDate?: string }).membershipYearEndDate ??
      (accountSummary.createdAt ? getMembershipYearEndDate(accountSummary.createdAt) : undefined),
    totalCreditsForTierRetention: accountSummary.totalCreditsForTierRetention ?? undefined,
    totalCreditsForTierUpgrade: accountSummary.totalCreditsForTierUpgrade ?? undefined,
    numberOfNights: accountSummary.numberOfNights,
    numberOfNightsForTierUpgrade: accountSummary.numberOfNightsForTierUpgrade ?? undefined,
    numberOfNightsForTierRetention: accountSummary.numberOfNightsForTierRetention ?? undefined,
    benefits: accountSummary.benefits.map(
      (benefit): App.LuxLoyaltyBenefit => ({
        code: benefit.code,
        description: benefit.description,
        details: {
          // convert 'unlimited' to infinity as it's a lot easier to work with numbers, not number | string
          remaining: benefit.details.remaining === 'unlimited' ? Infinity : benefit.details.remaining,
          limit: benefit.details.limit === 'unlimited' ? Infinity : benefit.details.limit,
          value: benefit.details.value
            ? {
                amount: benefit.details.value.amount,
                unit: benefit.details.value.unit,
                currency: benefit.details.value.currency,
              }
            : undefined,
        },
      }),
    ),
    region: accountSummary.region,
    customerServiceRep: accountSummary.customerServiceRep ?? null,
    infiniteProfile: accountSummary.infiniteProfile ?? null,
    earnDecisions,
  };
}

/** `errorCode`s svc-lux-loyalty returns when an account summary read is refused. */
export const LUX_LOYALTY_ACCOUNT_ERROR_CODES = {
  /** The account is closed. The body still carries the summary under `result.accountSummary`. */
  ACCOUNT_CLOSED: 'ACCOUNT_CLOSED',
  /** The IBS membership itself is closed or deleted. No summary. */
  ACCOUNT_MEMBERSHIP_CLOSED: 'ACCOUNT_MEMBERSHIP_CLOSED',
  /** A loyalty account exists but IBS has no member behind it. */
  ACCOUNT_IBS_MEMBER_NOT_FOUND: 'ACCOUNT_IBS_MEMBER_NOT_FOUND',
} as const;

const accountUnavailableMessages: Partial<Record<string, string>> = {
  [LUX_LOYALTY_ACCOUNT_ERROR_CODES.ACCOUNT_MEMBERSHIP_CLOSED]:
    'The IBS membership behind this account is closed or deleted, so the account cannot be shown.',
  [LUX_LOYALTY_ACCOUNT_ERROR_CODES.ACCOUNT_IBS_MEMBER_NOT_FOUND]:
    'This customer has a loyalty account, but IBS has no member behind it, so the account cannot be shown.',
};

function getErrorResponseBody(error: unknown): Record<string, unknown> | null {
  return isRecord(error) && isRecord(error.responseBody) ? error.responseBody : null;
}

export function getLuxLoyaltyErrorCode(error: unknown): string | null {
  const errorCode = getErrorResponseBody(error)?.errorCode;
  return typeof errorCode === 'string' ? errorCode : null;
}

/**
 * Message to show in place of the account when the lookup failed because the account exists but cannot be read,
 * rather than because the customer has no account. Null for any other error.
 */
export function getLuxLoyaltyAccountUnavailableMessage(error: unknown): string | null {
  const errorCode = getLuxLoyaltyErrorCode(error);
  return errorCode ? (accountUnavailableMessages[errorCode] ?? null) : null;
}

function isAccountSummary(value: unknown): value is GetLuxLoyaltyAccountResponse {
  return isRecord(value) && typeof value.customerId === 'string' && Array.isArray(value.benefits);
}

/** The account read's summary plus the earn decisions svc-lux-loyalty returns alongside it. */
interface FetchedAccount {
  accountSummary: GetLuxLoyaltyAccountResponse;
  earnDecisions: Array<App.LuxLoyaltyEarnDecisionGroupSummary>;
}

/** The account attached to an `ACCOUNT_CLOSED` refusal, or null when `error` is anything else. */
function getClosedAccount(error: unknown): FetchedAccount | null {
  const body = getErrorResponseBody(error);
  if (body?.errorCode !== LUX_LOYALTY_ACCOUNT_ERROR_CODES.ACCOUNT_CLOSED || !isRecord(body.result)) {
    return null;
  }
  const { accountSummary, earnDecisions } = body.result;
  return isAccountSummary(accountSummary)
    ? { accountSummary, earnDecisions: parseEarnDecisions(earnDecisions) }
    : null;
}

interface GetLuxLoyaltyAccountOptions {
  /**
   * Return a closed account (`accountStatus: 'closed'`) instead of throwing. svc-lux-loyalty refuses summary reads
   * of a closed account with `ACCOUNT_CLOSED` but attaches the summary; matching on the code, not the HTTP status,
   * keeps this working if that status changes.
   */
  includeClosed?: boolean;
}

async function fetchAccount(uri: string, includeClosed: boolean): Promise<FetchedAccount> {
  try {
    const { result } = await request(uri, { method: 'GET' });
    return { accountSummary: result.accountSummary, earnDecisions: parseEarnDecisions(result.earnDecisions) };
  } catch (error) {
    const closedAccount = includeClosed ? getClosedAccount(error) : null;
    if (closedAccount) {
      return closedAccount;
    }
    throw error;
  }
}

/**
 * Fetches a Lux Loyalty account mapped to `App.LuxLoyaltyAccount`. Returns null when Lux Loyalty
 * is disabled. Use {@link getLoyaltyAccount} for the raw contract response.
 */
export async function getLuxLoyaltyAccount(
  customerId: string,
  { includeClosed = false }: GetLuxLoyaltyAccountOptions = {},
): Promise<App.LuxLoyaltyAccount | null> {
  if (!customerId || !isLuxLoyaltyEnabled()) {
    return null;
  }

  const queryParams = qs.stringify({ customerId });
  const uri = `${BASE_PATH}/account?${queryParams}`;

  return mapAccountSummaryToLuxLoyaltyAccount(await fetchAccount(uri, includeClosed));
}

/**
 * Fetch a Lux Loyalty account using the loyalty account number.
 */
export async function getLuxLoyaltyAccountByNumber(
  accountNumber: number | string,
  { includeClosed = false }: GetLuxLoyaltyAccountOptions = {},
): Promise<App.LuxLoyaltyAccount | null> {
  if (!isLuxLoyaltyEnabled() || accountNumber == null) {
    return null;
  }

  const value = String(accountNumber).trim();
  const uri = `${BASE_PATH}/account/account-number/${value}`;

  return mapAccountSummaryToLuxLoyaltyAccount(await fetchAccount(uri, includeClosed));
}

/**
 * Fetches the raw account response. Unlike {@link getLuxLoyaltyAccount}, this is not gated on
 * Lux Loyalty being enabled and does not map the account summary.
 */
export async function getLoyaltyAccount(customerId: string): Promise<GetLoyaltyAccountResponse> {
  return request(`${BASE_PATH}/account?customerId=${customerId}`, {
    method: 'GET',
  });
}

export async function getLuxLoyaltyActivityLog(
  customerId: string,
  fromDate: string,
  toDate: string,
): Promise<App.LuxLoyaltyActivityLog | null> {
  const queryParams = qs.stringify({ customerId, fromDate, toDate });
  const uri = `${BASE_PATH}/account/activities?${queryParams}`;

  const response: GetLuxLoyaltyActivityLogResponse = await request(uri, { method: 'GET' });
  const { result } = response;

  return result.activities?.map((activity) => ({
    id: activity.id,
    activityDate: activity.activityDate,
    activityType: activity.activityType,
    activityDescription: activity.activityDescription,
    statusCredits: activity.statusCredits,
    bonusStatusCredits: activity.bonusStatusCredits,
    points: activity.basePoints,
    bonusPoints: activity.bonusPoints,
    numberOfNights: activity.numNights,
    currencyAmount: activity.currencyAmount,
    currency: activity.currency,
    orderId: activity.orderId,
    itemId: activity.itemId,
  }));
}

/**
 * Updates the region for a Lux Loyalty account
 * @param customerId - The customer ID whose region needs to be updated
 * @param region - The new region code (e.g., 'SG', 'NZ', 'AU')
 * @returns The updated account summary response or null if Lux Loyalty is not enabled
 * @throws {Error} When Lux Loyalty is not enabled or customer ID is missing
 */
export async function updateLuxLoyaltyAccountRegion(
  customerId: string,
  region: AccountUpdateRegionRequest['region'],
): Promise<AccountUpdateRegionResponse | null> {
  // Validate that Lux Loyalty is enabled and customer ID is provided
  if (!isLuxLoyaltyEnabled() || !customerId) {
    throw new Error('Lux Loyalty is not enabled or customer ID is missing');
  }

  const uri = `${BASE_PATH}/account/region`;
  const payload: AccountUpdateRegionRequest = {
    customerId,
    region,
  };

  // Make PATCH request to update the account region
  const response: AccountUpdateRegionResponse = await request(uri, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  return response;
}

export async function changeTier(params: TierChangeRequestBody): Promise<TierChangeResponse> {
  return request(`${BASE_PATH}/account/tier-change`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function enrolAccount(customerId: string): Promise<unknown> {
  return request(`${BASE_PATH}/account`, {
    method: 'POST',
    body: JSON.stringify({ customerId }),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function createLoyaltyAccount(params: CreateLoyaltyAccountRequest): Promise<CreateLoyaltyAccountResponse> {
  return request(`${BASE_PATH}/account`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Admin-only enrolment. When `tier` is omitted, the loyalty service applies
 * its default tier-resolution logic (prehandlers, falling back to bronze).
 * When `customerServiceRepId` is provided, the loyalty service links the
 * account to that rep as part of the enrolment. Backed by
 * POST /api/lux-loyalty/account/internal — restricted to admin and service
 * roles on the server side via checkUserAdminPermissions.
 */
export async function enrolAccountInternal(
  params: AccountInternalCreateRequest,
): Promise<AccountInternalCreateResponse> {
  return request(`${BASE_PATH}/account/internal`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Admin-only. Set or overwrite the customer service rep linked to an existing
 * Société account. Backed by
 * POST /api/lux-loyalty/account/{customerId}/customer-service-rep.
 */
export async function setAccountCustomerServiceRep(
  params: { customerId: string } & SetAccountCustomerServiceRepBody,
): Promise<unknown> {
  return request(`${BASE_PATH}/account/${encodeURIComponent(params.customerId)}/customer-service-rep`, {
    method: 'POST',
    body: JSON.stringify({ customerServiceRepId: params.customerServiceRepId }),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function updateInfiniteProfile(
  customerId: string,
  params: UpdateInfiniteProfileRequest,
): Promise<UpdateInfiniteProfileResponse> {
  return request(`${BASE_PATH}/account/${encodeURIComponent(customerId)}/infinite-profile`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function resendInfiniteOnboardingEmail(customerId: string): Promise<InfiniteOnboardingEmailResponse> {
  return request(`${BASE_PATH}/account/${encodeURIComponent(customerId)}/infinite-onboarding-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Admin-only. Close or re-open a Société account. `changed: false` in the result means the account was already in
 * that status and nothing was changed. Backed by PATCH /api/lux-loyalty/account/{customerId}/status.
 */
export async function updateAccountStatus(
  customerId: string,
  body: AccountStatusUpdateRequestBody,
): Promise<AccountStatusUpdateResponse> {
  return request(`${BASE_PATH}/account/${encodeURIComponent(customerId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Admin-only. One page (1-based) of an account's lifecycle changes, newest first. Backed by
 * GET /api/lux-loyalty/account/{customerId}/change-log.
 */
export async function getAccountChangeLog(
  customerId: string,
  { page, pageSize }: { page: number; pageSize: number },
  options: RequestOptions = {},
): Promise<AccountChangeLogResponse> {
  const query = qs.stringify({ page, pageSize });
  return request(`${BASE_PATH}/account/${encodeURIComponent(customerId)}/change-log?${query}`, {
    ...options,
    method: 'GET',
  });
}
